import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Add, EditOutlined } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import PageTitle from '../../page-title/PageTitle';
import NavAppbar from '../../nav-top/NavAppbar';
import {
  DocumentTemplateType,
  IInsertionSubscriberSecurityGroup,
  InboxPermissionLevel,
  ISecurityGroup,
  ISecurityGroupUserSummary,
  ISubscriber,
  PickIInsertionSecurityGroupDocumentTemplateExcludeKeyofIInsertionSecurityGroupDocumentTemplateSecurityGroupId,
  ProjectLifecyclePermissionLevel,
  SecurityPermissionLevel,
  TaskManagerPermission,
} from '../../../api-client/autogenerated';
import {
  deleteSecurityGroupById,
  editSecurityGroupBySubscriberId,
  getAllSecurityGroupsBySubscriberId,
  getProjectUserSummaryBySecurityGroupId,
  insertSecurityGroupBySubscriberId,
} from '../../../models/api/security-groups';
import { getUserState } from '../../../features/user/selectors';
import {
  CancelButton,
  MediumButton,
  SmallButton,
  SubmitButton,
} from '../../custom-components/CustomButtons';
import DashboardBlock from '../../admin-dashboard/DashboardBlock';
import PermissionControl from './PermissionControl';
import FormMode from './FormMode';
import DeleteSecurityGroupDialog from './DeleteSecurityGroupDialog';
import DeleteIcon from '@material-ui/icons/Delete';
import CircularLoader from '../../loader/CircularLoader';
import { addSnackbar } from '../../../features/snackbar/actions';
import { ascendingComparator } from '../../document-index/DocumentIndexUtils';
import { useHistory, useParams } from 'react-router';
import { useAppUtilsContext } from '../../AppUtilsContext';
import { getSubscriberById } from '../../../models/api/subscribers';
import { useCenterlineAiSubscriberAvailable } from '../../centerline-ai/centerline-ai-access';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    marginTop: 8,
    paddingBottom: 32,
  },
  submit: {
    margin: theme.spacing(1),
  },
  root: {
    flexGrow: 1,
    //padding: theme.spacing(3),
    minHeight: '100vh',
    [theme.breakpoints.down('sm')]: {
      padding: '12px 0px',
    },
    [theme.breakpoints.up('md')]: {
      padding: '24px',
    },
  },
  paper: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    width: '100%',
  },
  alignLeft: {
    textAlign: 'left',
  },
  paddingLeft: {
    paddingLeft: 15,
    paddingTop: 10,
  },
  checkbox: {
    paddingLeft: 25,
    marginLeft: 20,
  },
  loadingCenter: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
  },
  loadingNotCenter: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  loadingMargin: {
    paddingBottom: 16,
  },
  textField: {
    maxWidth: 300,
    paddingTop: 4,
    paddingBottom: 8,
  },
  textFieldSmall: {
    maxWidth: 142,
  },

  //TODO: get the screenwidth and make the maxWidth based on those calculations
  //padding: isForAdminDashboard ? '24px 0px' : '24px'
  rootGrid: {
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      width: '100vw',
      paddingTop: '',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      margin: 0,
      width: '90vw',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  formControl: {
    padding: 0,
  },
  fileInstructions: {
    maxWidth: 500,
  },
  endDiv: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 8,
    paddingBottom: 8,
  },
  widthConstraint: {
    maxWidth: 1300,
  },
  inline: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingBottom: 8,
  },
  pdf: {
    width: 32,
    height: 32,
    marginRight: 16,
  },
  deleteButton: {
    backgroundColor: '#ED3F26',
    '&:hover': {
      backgroundColor: '#FF5D45',
    },
    '&:focus': {
      backgroundColor: '#FF5D45',
    },
  },
}));

interface Props {
  isForAdminDashboard?: boolean;
}

type SecurityGroupsCardProps = {
  isForAdminDashboard?: boolean;
  className: string;
  children: React.ReactNode;
};

/**
 * The card these controls live in. On the subscriber admin dashboard they are one card among
 * several, so they take the same shell as the cards around them -- blue header, white title. On
 * their own page they keep the plain card that sits under the page title. Either way the card is
 * named above its content, so it carries no heading of its own.
 */
function SecurityGroupsCard(props: SecurityGroupsCardProps) {
  const { isForAdminDashboard, className, children } = props;

  if (!isForAdminDashboard) return <Card className={className}>{children}</Card>;

  return (
    <DashboardBlock title="Manage Security Groups">
      <div className={className}>{children}</div>
    </DashboardBlock>
  );
}

export default function SecurityGroupsPage(props: Props) {
  const classes = useStyles();
  const authUser = useSelector(getUserState);
  const dispatch = useDispatch();
  const { subscriberId } = useParams<{ subscriberId: string }>();
  const history = useHistory();

  const targetSubscriberId = subscriberId ?? authUser.adminOfSubscriberId;

  const {
    documentTemplates: { documentTemplateNameToId },
  } = useAppUtilsContext();

  const { isForAdminDashboard } = props;

  const [subscriber, setSubscriber] = useState<ISubscriber>();

  const [securityGroups, setSecurityGroups] = useState<ISecurityGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<ISecurityGroup | undefined>(undefined);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [securityGroupSummary, setSecurityGroupSummary] = useState<ISecurityGroupUserSummary>();

  const editAnchor = useRef<any>(null);
  const addAnchor = useRef<any>(null);

  const [mode, setMode] = useState<FormMode>(0);

  const [securityGroupInput, setSecurityGroupInput] = useState<string>('');
  const [tempName, setTempName] = useState<string>('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const setInitialState = (type: DocumentTemplateType) => {
    let val = null;
    if (selectedGroup) {
      try {
        val = selectedGroup!.securityGroupDocumentTemplateList!.find(
          (x) => x.documentTemplateId === documentTemplateNameToId[type],
        )!.securityPermissionLevel!;
      } catch (err: any) {
        val = null;
      }
    }
    return val;
  };

  // Task / CA Task templates store their access scope in taskManagerPermission
  // (None/Personal/Company/Everyone/Admin), separate from securityPermissionLevel.
  const setInitialTaskState = (type: DocumentTemplateType): TaskManagerPermission | null => {
    if (selectedGroup) {
      try {
        return (
          selectedGroup!.securityGroupDocumentTemplateList!.find(
            (x) => x.documentTemplateId === documentTemplateNameToId[type],
          )!.taskManagerPermission ?? null
        );
      } catch (err: any) {
        return null;
      }
    }
    return null;
  };

  const [userGroups, setUserGroups] = useState<boolean>(false);
  const [manageProjectUsers, setManageProjectUsers] = useState<SecurityPermissionLevel | null>(
    null,
  );

  const [correspondenceLog, setCorrespondenceLog] = useState<InboxPermissionLevel | null>(null);

  const [design, setDesign] = useState<SecurityPermissionLevel | null>(null);

  const [bidManagement, setBidManagement] = useState<boolean>(false);
  const [bidTabulation, setBidTabulation] = useState<boolean>(false);
  const [planholderList, setPlanholderList] = useState<boolean>(false);
  const [bidDrawings, setBidDrawings] = useState<SecurityPermissionLevel | null>(null);
  const [projectManual, setProjectManual] = useState<SecurityPermissionLevel | null>(null);
  const [addenda, setAddenda] = useState<SecurityPermissionLevel | null>(null);
  const [informationalItems, setInformationalItems] = useState<SecurityPermissionLevel | null>(
    null,
  );
  const [substitutionRequests, setSubstitutionRequests] = useState<SecurityPermissionLevel | null>(
    null,
  );
  const [bidderRFIs, setBidderRFIs] = useState<SecurityPermissionLevel | null>(null);

  const [regulatoryApprovals, setRegulatoryApprovals] = useState<SecurityPermissionLevel | null>(
    null,
  );

  const [constructionManagement, setConstructionManagement] = useState<boolean>(false);
  const [publishingCenter, setPublishingCenter] = useState<boolean>(false);
  const [drawings, setDrawings] = useState<SecurityPermissionLevel | null>(null);
  const [specifications, setSpecifications] = useState<SecurityPermissionLevel | null>(null);
  const [ASIDocuments, setASIDocuments] = useState<SecurityPermissionLevel | null>(null);
  const [submittals, setSubmittals] = useState<SecurityPermissionLevel | null>(null);
  const [
    requestsForInformation,
    setRequestsForInformation,
  ] = useState<SecurityPermissionLevel | null>(null);
  const [changeOrders, setChangeOrders] = useState<SecurityPermissionLevel | null>(null);
  const [
    constChangeDirectives,
    setConstChangeDirectives,
  ] = useState<SecurityPermissionLevel | null>(null);
  const [
    potentialChangeOrders,
    setPotentialChangeOrders,
  ] = useState<SecurityPermissionLevel | null>(null);
  const [requestsForChange, setRequestsForChange] = useState<SecurityPermissionLevel | null>(null);
  const [
    workChangeProposalReq,
    setWorkChangeProposalReq,
  ] = useState<SecurityPermissionLevel | null>(null);
  const [payApplications, setPayApplications] = useState<SecurityPermissionLevel | null>(null);
  const [contractorDailyLogs, setContractorDailyLogs] = useState<SecurityPermissionLevel | null>(
    null,
  );
  const [fieldReports, setFieldReports] = useState<SecurityPermissionLevel | null>(null);
  const [meetingMinutes, setMeetingMinutes] = useState<SecurityPermissionLevel | null>(null);
  const [
    miscellaneousDocuments,
    setMiscellaneousDocuments,
  ] = useState<SecurityPermissionLevel | null>(null);
  const [schedules, setSchedules] = useState<SecurityPermissionLevel | null>(null);
  const [testing, setTesting] = useState<SecurityPermissionLevel | null>(null);
  const [caTasks, setCaTasks] = useState<TaskManagerPermission | null>(null);
  const [additionalReview, setAdditionalReview] = useState<SecurityPermissionLevel | null>(null);

  const [asBuilt, setAsBuilt] = useState<SecurityPermissionLevel | null>(null);
  const [closeoutSubmittals, setCloseoutSubmittals] = useState<SecurityPermissionLevel | null>(
    null,
  );
  const [OMData, setOMData] = useState<SecurityPermissionLevel | null>(null);
  const [punchList, setPunchList] = useState<SecurityPermissionLevel | null>(null);
  const [warrantyItems, setWarrantyItems] = useState<SecurityPermissionLevel | null>(null);
  const [
    substantialCompletionCert,
    setSubstantialCompletionCert,
  ] = useState<SecurityPermissionLevel | null>(null);
  const [clearLP, setClearLP] = useState<SecurityPermissionLevel | null>(null);
  const [tasks, setTasks] = useState<TaskManagerPermission | null>(null);
  // "User can review/complete tasks?" (security_group.taskAssignmentPermission)
  const [taskAssignment, setTaskAssignment] = useState<boolean>(false);
  const [designPackages, setDesignPackages] = useState<SecurityPermissionLevel | null>(null);
  const [designMeetingMinutes, setDesignMeetingMinutes] = useState<SecurityPermissionLevel | null>(
    null,
  );

  const [projectLifecycle, setProjectLifecycle] = useState<ProjectLifecyclePermissionLevel | null>(
    null,
  );
  const [search, setSearch] = useState<SecurityPermissionLevel | null>(null);
  // security_group.canUseCenterlineAi. Shown only while the subscriber is entitled to CENTERLINE AI
  // (and, during the production test, to site admins), and saved only when shown.
  const [centerlineAi, setCenterlineAi] = useState(false);
  const centerlineAiAvailable = useCenterlineAiSubscriberAvailable(targetSubscriberId);

  const getSelectedGroupName = () => {
    let str;
    try {
      str = securityGroups.find((x) => x.id === selectedGroupId)!.name;
    } catch {
      str = '';
    }
    return str;
  };

  useEffect(() => {
    if (mode !== FormMode.CREATE) {
      setSelectedGroup(securityGroups.find((x) => x.id === selectedGroupId));
      setSecurityGroupInput(getSelectedGroupName);
      if (selectedGroupId) {
        setDeleteLoading(true);
        getProjectUserSummaryBySecurityGroupId(selectedGroupId).then((summary) => {
          setSecurityGroupSummary(summary);
          setDeleteLoading(false);
        });
      }
    }
  }, [selectedGroupId]);

  useEffect(() => {
    if (!authUser.id) return;
    if (subscriberId && subscriberId !== authUser.adminOfSubscriberId && !authUser.isSiteAdmin) {
      history.push('/main');
    }
  }, [authUser, subscriberId, history]);

  useEffect(() => {
    if (!targetSubscriberId) return;

    getAllSecurityGroupsBySubscriberId(targetSubscriberId).then(setSecurityGroups);
    getSubscriberById(targetSubscriberId).then(setSubscriber);
  }, [targetSubscriberId]);

  useEffect(() => {
    if (mode !== FormMode.CREATE && selectedGroup) {
      setUserGroups(selectedGroup.canModifyUserGroups || false);
      setManageProjectUsers(selectedGroup.manageProjectUsersPermission ?? null);

      setCorrespondenceLog(selectedGroup.correspondenceInboxPermission ?? null);

      setDesign(selectedGroup.designTabPermission ?? null);
      setTasks(setInitialTaskState(DocumentTemplateType.Task));
      setCaTasks(setInitialTaskState(DocumentTemplateType.CaTask));
      setTaskAssignment(selectedGroup.taskAssignmentPermission || false);
      setDesignPackages(setInitialState(DocumentTemplateType.DesignPackages));
      setDesignMeetingMinutes(setInitialState(DocumentTemplateType.DesignMeetingMinutes));

      setBidManagement(!!selectedGroup.bidManagementPermission);
      setBidDrawings(setInitialState(DocumentTemplateType.BidDrawings));
      setProjectManual(setInitialState(DocumentTemplateType.ProjectManual));
      setAddenda(setInitialState(DocumentTemplateType.Addenda));
      setInformationalItems(setInitialState(DocumentTemplateType.InformationalItems));
      setSubstitutionRequests(setInitialState(DocumentTemplateType.SubstitutionRequests));
      setBidderRFIs(setInitialState(DocumentTemplateType.BidderRfIs));
      setPlanholderList(!!selectedGroup.planholderListPermission);
      setBidTabulation(!!selectedGroup.bidTabulationPermission);
      setRegulatoryApprovals(setInitialState(DocumentTemplateType.RegulatoryApprovals));

      setConstructionManagement(!!selectedGroup.constructionManagementPermission);
      setPublishingCenter(!!selectedGroup.publishingCenterPermission);
      setDrawings(setInitialState(DocumentTemplateType.Drawings));
      setSpecifications(setInitialState(DocumentTemplateType.Specifications));
      setASIDocuments(setInitialState(DocumentTemplateType.AsiDocuments));
      setSubmittals(setInitialState(DocumentTemplateType.Submittals));
      setRequestsForInformation(setInitialState(DocumentTemplateType.RequestsForInformation));
      setChangeOrders(setInitialState(DocumentTemplateType.ChangeOrders));
      setConstChangeDirectives(setInitialState(DocumentTemplateType.ConstructionChangeDirectives));
      setPotentialChangeOrders(setInitialState(DocumentTemplateType.PotentialChangeOrders));
      setRequestsForChange(setInitialState(DocumentTemplateType.RequestsForChange));
      setWorkChangeProposalReq(setInitialState(DocumentTemplateType.WorkChangeProposalRequests));
      setPayApplications(setInitialState(DocumentTemplateType.PayApplications));
      setContractorDailyLogs(setInitialState(DocumentTemplateType.ContractorDailyLogs));
      setFieldReports(setInitialState(DocumentTemplateType.FieldReports));
      setMeetingMinutes(setInitialState(DocumentTemplateType.MeetingMinutes));
      setMiscellaneousDocuments(setInitialState(DocumentTemplateType.MiscellaneousDocuments));
      setSchedules(setInitialState(DocumentTemplateType.Schedules));
      setTesting(setInitialState(DocumentTemplateType.Testing));
      setAdditionalReview(setInitialState(DocumentTemplateType.AdditionalReview));

      setAsBuilt(setInitialState(DocumentTemplateType.AsBuilt));
      setCloseoutSubmittals(setInitialState(DocumentTemplateType.CloseoutSubmittals));
      setOMData(setInitialState(DocumentTemplateType.OperationsAndMaintenanceData));
      setPunchList(setInitialState(DocumentTemplateType.PunchList));
      setWarrantyItems(setInitialState(DocumentTemplateType.WarrantyItems));
      setSubstantialCompletionCert(
        setInitialState(DocumentTemplateType.CertificateOfSubstantialCompletion),
      );
      setClearLP(setInitialState(DocumentTemplateType.ClearLp));

      setProjectLifecycle(selectedGroup.projectLifecyclePermission ?? null);
      setSearch(selectedGroup.searchPermission ?? null);
      setCenterlineAi(!!selectedGroup.canUseCenterlineAi);
    } else {
      setUserGroups(false);
      setManageProjectUsers(null);

      setCorrespondenceLog(null);

      setDesign(null);
      setTasks(null);
      setCaTasks(null);
      setTaskAssignment(false);
      setDesignPackages(null);
      setDesignMeetingMinutes(null);

      setBidManagement(false);
      setBidDrawings(null);
      setProjectManual(null);
      setAddenda(null);
      setInformationalItems(null);
      setSubstitutionRequests(null);
      setBidderRFIs(null);
      setPlanholderList(false);
      setBidTabulation(false);
      setRegulatoryApprovals(null);

      setConstructionManagement(false);
      setPublishingCenter(false);
      setDrawings(null);
      setSpecifications(null);
      setASIDocuments(null);
      setSubmittals(null);
      setRequestsForInformation(null);
      setChangeOrders(null);
      setConstChangeDirectives(null);
      setPotentialChangeOrders(null);
      setRequestsForChange(null);
      setWorkChangeProposalReq(null);
      setPayApplications(null);
      setContractorDailyLogs(null);
      setFieldReports(null);
      setMeetingMinutes(null);
      setMiscellaneousDocuments(null);
      setSchedules(null);
      setTesting(null);
      setAdditionalReview(null);

      setAsBuilt(null);
      setCloseoutSubmittals(null);
      setOMData(null);
      setPunchList(null);
      setWarrantyItems(null);
      setSubstantialCompletionCert(null);
      setClearLP(null);

      setProjectLifecycle(null);
      setSearch(null);
      setCenterlineAi(false);
    }
  }, [selectedGroup, mode]);

  const createSecurityGroup = (): IInsertionSubscriberSecurityGroup => {
    const docPerms: PickIInsertionSecurityGroupDocumentTemplateExcludeKeyofIInsertionSecurityGroupDocumentTemplateSecurityGroupId[] = [];
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.BidDrawings],
      securityPermissionLevel: bidDrawings,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.ProjectManual],
      securityPermissionLevel: projectManual,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.Addenda],
      securityPermissionLevel: addenda,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.InformationalItems],
      securityPermissionLevel: informationalItems,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.SubstitutionRequests],
      securityPermissionLevel: substitutionRequests,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.BidderRfIs],
      securityPermissionLevel: bidderRFIs,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.PlanholderList],
      securityPermissionLevel: planholderList ? SecurityPermissionLevel.NUMBER_4 : null,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.BidTabulation],
      securityPermissionLevel: bidTabulation ? SecurityPermissionLevel.NUMBER_4 : null,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.RegulatoryApprovals],
      securityPermissionLevel: regulatoryApprovals,
    });

    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.Drawings],
      securityPermissionLevel: drawings,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.Specifications],
      securityPermissionLevel: specifications,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.AsiDocuments],
      securityPermissionLevel: ASIDocuments,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.Submittals],
      securityPermissionLevel: submittals,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.SubmittalPackages],
      securityPermissionLevel: submittals,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.RequestsForInformation],
      securityPermissionLevel: requestsForInformation,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.ChangeOrders],
      securityPermissionLevel: changeOrders,
    });
    docPerms.push({
      documentTemplateId:
        documentTemplateNameToId[DocumentTemplateType.ConstructionChangeDirectives],
      securityPermissionLevel: constChangeDirectives,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.PotentialChangeOrders],
      securityPermissionLevel: potentialChangeOrders,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.RequestsForChange],
      securityPermissionLevel: requestsForChange,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.WorkChangeProposalRequests],
      securityPermissionLevel: workChangeProposalReq,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.PayApplications],
      securityPermissionLevel: payApplications,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.ContractorDailyLogs],
      securityPermissionLevel: contractorDailyLogs,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.FieldReports],
      securityPermissionLevel: fieldReports,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.MeetingMinutes],
      securityPermissionLevel: meetingMinutes,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.MiscellaneousDocuments],
      securityPermissionLevel: miscellaneousDocuments,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.Schedules],
      securityPermissionLevel: schedules,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.Testing],
      securityPermissionLevel: testing,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.AdditionalReview],
      securityPermissionLevel: additionalReview,
    });

    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.AsBuilt],
      securityPermissionLevel: asBuilt,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.AsBuiltPackages],
      securityPermissionLevel: asBuilt,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.CloseoutSubmittals],
      securityPermissionLevel: closeoutSubmittals,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.CloseoutSubmittalPackages],
      securityPermissionLevel: closeoutSubmittals,
    });
    docPerms.push({
      documentTemplateId:
        documentTemplateNameToId[DocumentTemplateType.OperationsAndMaintenanceData],
      securityPermissionLevel: OMData,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.PunchList],
      securityPermissionLevel: punchList,
    });
    // docPerms.push({
    //   documentTemplateId: documentTemplateNameToId['Punch List Setup'],
    //   securityPermissionLevel: punchListSetup,
    // });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.WarrantyItems],
      securityPermissionLevel: warrantyItems,
    });
    docPerms.push({
      documentTemplateId:
        documentTemplateNameToId[DocumentTemplateType.CertificateOfSubstantialCompletion],
      securityPermissionLevel: substantialCompletionCert,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.ClearLp],
      securityPermissionLevel: clearLP,
    });
    // Shadow securityPermissionLevel so existing document gates apply to tasks: None -> null
    // (hidden); read-only (VIEW) unless the group can review/complete tasks, then ALL. The
    // backend re-derives this authoritatively on save; we mirror it here for immediate UI state.
    const taskShadow = (level: TaskManagerPermission | null) =>
      level == null
        ? null
        : taskAssignment
        ? SecurityPermissionLevel.NUMBER_4
        : SecurityPermissionLevel.NUMBER_0;
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.Task],
      taskManagerPermission: tasks,
      securityPermissionLevel: taskShadow(tasks),
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.CaTask],
      taskManagerPermission: caTasks,
      securityPermissionLevel: taskShadow(caTasks),
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.DesignPackages],
      securityPermissionLevel: designPackages,
    });
    docPerms.push({
      documentTemplateId: documentTemplateNameToId[DocumentTemplateType.DesignMeetingMinutes],
      securityPermissionLevel: designMeetingMinutes,
    });

    return {
      name: securityGroupInput,
      securityGroupDocumentTemplateList: docPerms,
      canModifyUserGroups: userGroups,
      designTabPermission: design,
      constructionManagementPermission: constructionManagement
        ? SecurityPermissionLevel.NUMBER_4
        : null,
      publishingCenterPermission: publishingCenter ? SecurityPermissionLevel.NUMBER_4 : null,
      planholderListPermission: planholderList ? SecurityPermissionLevel.NUMBER_4 : null,
      bidManagementPermission: bidManagement ? SecurityPermissionLevel.NUMBER_4 : null,
      bidTabulationPermission: bidTabulation ? SecurityPermissionLevel.NUMBER_4 : null,
      manageProjectUsersPermission: manageProjectUsers ? SecurityPermissionLevel.NUMBER_4 : null,
      correspondenceInboxPermission: correspondenceLog,
      taskAssignmentPermission: taskAssignment,
      ...(subscriber?.canViewProjectLifecycle
        ? { projectLifecyclePermission: projectLifecycle }
        : {}),
      ...(centerlineAiAvailable ? { canUseCenterlineAi: centerlineAi } : {}),
      searchPermission: search,
    };
  };

  // Item 5: "User can review/complete tasks?" = Yes requires at least Personal on Task or CA Task.
  const taskAssignmentTooltip =
    'To review or complete a task, users must have at least "Personal" level permission on ' +
    'EITHER Design Task Manager or CA Task Manager';
  const taskAssignmentInvalid = taskAssignment && !tasks && !caTasks;

  const validateBeforeSave = (): boolean => {
    if (taskAssignmentInvalid) {
      dispatch(addSnackbar({ id: Date.now(), severity: 'error', message: taskAssignmentTooltip }));
      return false;
    }
    return true;
  };

  const insertSecurityGroup = () => {
    if (!targetSubscriberId) return;
    if (!validateBeforeSave()) return;
    const securityGroup = createSecurityGroup();

    insertSecurityGroupBySubscriberId(targetSubscriberId, securityGroup).then((res) => {
      setMode(FormMode.NONE);
      const newSecurityGroups = [...securityGroups];
      newSecurityGroups.push(res);
      setSecurityGroups(newSecurityGroups);
      setSelectedGroupId(res.id);
      setSelectedGroup(res);
    });
  };

  const patchSecurityGroup = () => {
    if (!targetSubscriberId) return;
    if (!validateBeforeSave()) return;
    const securityGroup = createSecurityGroup();

    editSecurityGroupBySubscriberId(targetSubscriberId, selectedGroupId!, securityGroup).then(
      (res) => {
        setMode(FormMode.NONE);
        const newSecurityGroups = [...securityGroups];
        const index = newSecurityGroups.findIndex((x) => x.id === selectedGroupId);
        newSecurityGroups[index] = res;
        setSecurityGroups(newSecurityGroups);
        setSelectedGroupId(res.id);
        setSelectedGroup(res);
      },
    );
  };

  const handleDelete = async () => {
    if (!securityGroupSummary || !selectedGroupId) return;

    if (securityGroupSummary.users.length === 0) {
      try {
        await deleteSecurityGroupById(selectedGroupId);
        setSelectedGroupId('');
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: 'Security group successfully deleted!',
            severity: 'success',
          }),
        );
        setSecurityGroups((groups) => groups.filter((g) => g.id !== selectedGroupId));
      } catch (e: any) {
        console.error(e);
      }
    } else {
      setDeleteDialogOpen(true);
    }
  };

  const transferUsersAndDelete = async (newSecurityGroupId: string) => {
    if (!securityGroupSummary || !selectedGroupId) return;

    try {
      await deleteSecurityGroupById(selectedGroupId, newSecurityGroupId);
      setDeleteDialogOpen(false);
      setSelectedGroupId('');
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Users transferred and old security group successfully deleted!',
          severity: 'success',
        }),
      );
      setSecurityGroups((groups) => groups.filter((g) => g.id !== selectedGroupId));
    } catch (e: any) {
      console.error(e);
    }
  };

  const getToolbarButtons = () => {
    if (mode === FormMode.NONE)
      return (
        <>
          <SmallButton
            variant="contained"
            color="primary"
            className={classes.deleteButton}
            style={{ marginRight: '8px' }}
            onClick={handleDelete}
            disabled={
              !selectedGroupId ||
              deleteLoading ||
              selectedGroupId === subscriber?.companyViewSecurityGroupId
            }
          >
            {deleteLoading ? (
              <CircularLoader
                size={16}
                loaderStyle={{ color: 'white' }}
                style={{ marginLeft: 4, marginRight: 4 }}
              />
            ) : (
              <DeleteIcon style={{ width: 20, height: 20, paddingRight: 2 }} />
            )}
            Delete
          </SmallButton>
          <SmallButton
            variant="contained"
            color="primary"
            ref={editAnchor}
            style={{ marginRight: '4px' }}
            onClick={() => {
              setMode(FormMode.EDIT);
              setTempName(securityGroupInput);
            }}
            disabled={!selectedGroupId}
          >
            <EditOutlined style={{ width: 20, height: 20, paddingRight: 2 }} />
            Edit
          </SmallButton>
          <MediumButton
            variant="contained"
            color="primary"
            ref={addAnchor}
            style={{ marginLeft: '4px', marginRight: '4px' }}
            onClick={() => {
              setMode(FormMode.CREATE);
              setSecurityGroupInput('');
            }}
          >
            <Add style={{ paddingRight: 2 }} />
            CREATE
          </MediumButton>
        </>
      );

    if (mode === FormMode.EDIT)
      return (
        <>
          <CancelButton
            style={{ marginRight: '4px', height: 32 }}
            onClick={() => {
              setMode(FormMode.NONE);
            }}
          >
            CANCEL
          </CancelButton>
          {tempName === securityGroupInput ? (
            <SubmitButton
              variant="contained"
              style={{ minWidth: '100px', marginLeft: '4px', height: 32 }}
              onClick={patchSecurityGroup}
              disabled={taskAssignmentInvalid}
            >
              SAVE CHANGES
            </SubmitButton>
          ) : (
            <SubmitButton
              variant="contained"
              style={{ minWidth: '100px', marginLeft: '4px', height: 32 }}
              onClick={insertSecurityGroup}
              disabled={taskAssignmentInvalid}
            >
              Create Group
            </SubmitButton>
          )}
        </>
      );

    // FormMode.CREATE
    return (
      <>
        <CancelButton
          style={{ marginRight: '4px', height: 32 }}
          onClick={() => {
            setMode(FormMode.NONE);
          }}
        >
          CANCEL
        </CancelButton>
        <SubmitButton
          variant="contained"
          style={{ minWidth: '100px', marginLeft: '4px', height: 32 }}
          onClick={insertSecurityGroup}
          disabled={taskAssignmentInvalid}
        >
          Create Group
        </SubmitButton>
      </>
    );
  };

  return (
    //Had to remove this padding for Mobile: padding: isForAdminDashboard ? '24px 0px' : '24px'
    <main
      className={classes.root}
      // On the admin dashboard this is one card in a stack rather than a page of its own, so drop
      // the page padding and full-height that would inset it from the cards around it.
      style={isForAdminDashboard ? { padding: 0, minHeight: 'unset' } : undefined}
    >
      {!isForAdminDashboard && (
        <>
          <NavAppbar />
          <Grid container justify="center" sm={12}>
            <Grid item xs={12} style={{ paddingLeft: isMobile ? 10 : 0 }}>
              <PageTitle title="Manage Security Groups" />
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={3} justify="center" className={classes.rootGrid}>
        <Grid item xs={12} container>
          <SecurityGroupsCard isForAdminDashboard={isForAdminDashboard} className={classes.paper}>
            <form className={classes.form}>
              <div className={classes.widthConstraint}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Selected Security Group</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <div
                      style={{
                        display: 'flex',
                        paddingBottom: 16,
                        alignItems: 'center',
                        justifyContent: isMobile ? 'center' : 'initial',
                      }}
                    >
                      <FormControl>
                        {mode === FormMode.NONE ? (
                          <Select
                            variant="outlined"
                            defaultValue=""
                            value={selectedGroupId}
                            displayEmpty
                            style={{ minWidth: 300, marginRight: 24 }}
                            onChange={(event) => setSelectedGroupId(event.target.value as string)}
                          >
                            <MenuItem value="" disabled>
                              Select Security Group
                            </MenuItem>
                            {securityGroups
                              ?.slice()
                              .filter(
                                (group) =>
                                  authUser.isSiteAdmin ||
                                  group.id !== subscriber?.companyViewSecurityGroupId,
                              )
                              .sort((a, b) => {
                                const companyViewId = subscriber?.companyViewSecurityGroupId;
                                if (companyViewId) {
                                  if (a.id === companyViewId) return -1;
                                  if (b.id === companyViewId) return 1;
                                }
                                return ascendingComparator(a, b, 'name');
                              })
                              .map((group) => {
                                return (
                                  <MenuItem
                                    key={group.id}
                                    value={group.id}
                                    style={{
                                      fontWeight:
                                        group.id === subscriber?.companyViewSecurityGroupId
                                          ? 'bold'
                                          : 'normal',
                                    }}
                                  >
                                    {group.name}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        ) : (
                          <TextField
                            variant="outlined"
                            id="security-group-name"
                            placeholder="Security Group Name"
                            name="Security Group Name"
                            defaultValue={securityGroupInput}
                            required
                            onBlur={(e) => setSecurityGroupInput(e.target.value)}
                            style={{ minWidth: 300, marginRight: 24 }}
                          />
                        )}
                      </FormControl>

                      {!isMobile && getToolbarButtons()}
                    </div>
                    {isMobile && (
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {' '}
                        {getToolbarButtons()}{' '}
                      </div>
                    )}
                    <Divider light style={{ marginTop: 16, marginBottom: 16 }} />

                    <div style={{ paddingBottom: 16, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        AI Features
                      </Typography>
                      {centerlineAiAvailable ? (
                        <>
                          {/* security_group.canUseCenterlineAi: a yes/no, shown in the Admin column. */}
                          <PermissionControl
                            label="CENTERLINE AI"
                            disabled={mode === FormMode.NONE}
                            value={centerlineAi ? SecurityPermissionLevel.NUMBER_4 : null}
                            disableView
                            disableComment
                            disableCreate
                            disableReview
                            setValue={(value) =>
                              setCenterlineAi(value !== null && value !== undefined)
                            }
                          />
                        </>
                      ) : null}
                      {/* The search permission, stored as Admin (NUMBER_4) as before; shown in the
                          second column as Enable. */}
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="AI Word Finder"
                        disableComment
                        disableCreate
                        disableReview
                        disableAdmin
                        customLabels={{ viewLabel: 'Enable' }}
                        customValues={{ viewValue: SecurityPermissionLevel.NUMBER_4 }}
                        value={search}
                        setValue={setSearch}
                      />
                      <Divider
                        style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                      />
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        Users
                      </Typography>
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="User Groups"
                        disableView
                        disableComment
                        disableCreate
                        disableReview
                        value={userGroups ? SecurityPermissionLevel.NUMBER_4 : null}
                        setValue={(value) => setUserGroups(!!value)}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Manage Project Users"
                        disableView
                        disableCreate
                        disableComment
                        disableReview
                        value={manageProjectUsers}
                        setValue={setManageProjectUsers}
                      />
                      <Divider
                        style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                      />
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        Email
                      </Typography>
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Correspondence Log"
                        disableComment
                        value={correspondenceLog}
                        setValue={
                          setCorrespondenceLog as React.Dispatch<
                            React.SetStateAction<SecurityPermissionLevel | null>
                          >
                        }
                        customLabels={{
                          viewLabel: 'Personal',
                          createLabel: 'Company',
                          reviewLabel: 'Everyone',
                        }}
                      />
                      <Divider
                        style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                      />
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        Tasks
                      </Typography>
                      <FormControl disabled={mode === FormMode.NONE}>
                        <Grid container alignItems="center">
                          <Grid item xs={'auto'} lg={'auto'}>
                            <Typography
                              style={{
                                width: 250,
                                lineHeight: isMobile ? 'initial' : '42px',
                                marginRight: isMobile ? 10 : 0,
                                paddingTop: isMobile ? 12 : 0,
                              }}
                            >
                              User can review/complete tasks?
                            </Typography>
                          </Grid>
                          <Grid item xs lg>
                            <RadioGroup
                              row
                              style={{ width: '100%' }}
                              value={taskAssignment ? 'yes' : 'no'}
                              onChange={(event, val) => setTaskAssignment(val === 'yes')}
                            >
                              <Grid item xs={12} lg>
                                <FormControlLabel
                                  value="no"
                                  control={<Radio color="primary" />}
                                  label="No"
                                />
                              </Grid>
                              <Grid item xs={12} lg>
                                <FormControlLabel
                                  value="yes"
                                  control={<Radio color="primary" />}
                                  label="Yes"
                                />
                              </Grid>
                              {/* Empty columns so No/Yes align with the first two permission
                                  columns (No, then Yes right next to it). */}
                              <Grid item xs={12} lg />
                              <Grid item xs={12} lg />
                              <Grid item xs={12} lg />
                              <Grid item xs={12} lg />
                            </RadioGroup>
                          </Grid>
                        </Grid>
                      </FormControl>
                      {taskAssignmentInvalid && (
                        <Typography
                          variant="caption"
                          style={{ color: theme.palette.error.main, paddingBottom: 8 }}
                        >
                          {taskAssignmentTooltip}
                        </Typography>
                      )}
                      <Divider
                        style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                      />
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        Design
                      </Typography>
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="CENTERLINE Drive"
                        disableCreate
                        disableComment
                        disableReview
                        value={design}
                        setValue={setDesign}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Design Task Manager"
                        disableComment
                        value={tasks}
                        setValue={setTasks}
                        customLabels={{
                          viewLabel: 'Personal',
                          createLabel: 'Company',
                          reviewLabel: 'Everyone',
                          adminLabel: 'Admin',
                        }}
                        customValues={{
                          viewValue: SecurityPermissionLevel.NUMBER_1,
                          createValue: SecurityPermissionLevel.NUMBER_2,
                          reviewValue: SecurityPermissionLevel.NUMBER_3,
                        }}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Design Meeting Minutes"
                        disableReview
                        value={designMeetingMinutes}
                        setValue={setDesignMeetingMinutes}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Design Package Review"
                        value={designPackages}
                        setValue={setDesignPackages}
                      />
                      <Divider
                        style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                      />
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        Bidding
                      </Typography>
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Bid Setup & Management"
                        disableView
                        disableCreate
                        disableComment
                        disableReview
                        value={bidManagement ? SecurityPermissionLevel.NUMBER_4 : null}
                        setValue={(value) => setBidManagement(!!value)}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Bid Submissions"
                        disableView
                        disableCreate
                        disableComment
                        disableReview
                        value={bidTabulation ? SecurityPermissionLevel.NUMBER_4 : null}
                        setValue={(value) => setBidTabulation(!!value)}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Planholder List"
                        disableCreate
                        disableComment
                        disableReview
                        disableAdmin
                        value={planholderList ? SecurityPermissionLevel.NUMBER_0 : null}
                        setValue={(value) => setPlanholderList(value !== null)}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Bid Drawings"
                        disableReview
                        value={bidDrawings}
                        setValue={setBidDrawings}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Project Manual"
                        disableReview
                        value={projectManual}
                        setValue={setProjectManual}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Addenda"
                        disableReview
                        value={addenda}
                        setValue={setAddenda}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Informational Items"
                        disableReview
                        value={informationalItems}
                        setValue={setInformationalItems}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Substitution Requests"
                        value={substitutionRequests}
                        setValue={setSubstitutionRequests}
                      />
                      <PermissionControl
                        label="Bidder RFIs"
                        value={bidderRFIs}
                        disabled={mode === FormMode.NONE}
                        setValue={setBidderRFIs}
                      />
                      {/*<PermissionControl*/}
                      {/*  disabled={mode === FormMode.NONE}*/}
                      {/*  label="Regulatory Approvals"*/}
                      {/*  disableReview*/}
                      {/*  value={regulatoryApprovals}*/}
                      {/*  setValue={setRegulatoryApprovals}*/}
                      {/*/>*/}

                      <Divider
                        style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                      />
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        Construction
                      </Typography>

                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Construction Management"
                        disableView
                        disableCreate
                        disableComment
                        disableReview
                        value={constructionManagement ? SecurityPermissionLevel.NUMBER_4 : null}
                        setValue={(value) => setConstructionManagement(value !== null)}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Publishing and Conforming Center"
                        disableView
                        disableCreate
                        disableComment
                        disableReview
                        value={publishingCenter ? SecurityPermissionLevel.NUMBER_4 : null}
                        setValue={(value) => setPublishingCenter(value !== null)}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Drawings"
                        disableReview
                        value={drawings}
                        setValue={setDrawings}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Specifications"
                        disableReview
                        value={specifications}
                        setValue={setSpecifications}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="ASI Documents"
                        disableReview
                        value={ASIDocuments}
                        setValue={setASIDocuments}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="CA Task Manager"
                        disableComment
                        value={caTasks}
                        setValue={setCaTasks}
                        customLabels={{
                          viewLabel: 'Personal',
                          createLabel: 'Company',
                          reviewLabel: 'Everyone',
                          adminLabel: 'Admin',
                        }}
                        customValues={{
                          viewValue: SecurityPermissionLevel.NUMBER_1,
                          createValue: SecurityPermissionLevel.NUMBER_2,
                          reviewValue: SecurityPermissionLevel.NUMBER_3,
                        }}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Submittals"
                        value={submittals}
                        setValue={setSubmittals}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Requests for Information"
                        value={requestsForInformation}
                        setValue={setRequestsForInformation}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Change Orders"
                        disableReview
                        value={changeOrders}
                        setValue={setChangeOrders}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Const. Change Directives"
                        disableReview
                        value={constChangeDirectives}
                        setValue={setConstChangeDirectives}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Potential Change Orders"
                        value={potentialChangeOrders}
                        setValue={setPotentialChangeOrders}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Requests for Change"
                        value={requestsForChange}
                        setValue={setRequestsForChange}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Work Change Proposal Req."
                        value={workChangeProposalReq}
                        setValue={setWorkChangeProposalReq}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Pay Applications"
                        value={payApplications}
                        setValue={setPayApplications}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Contractor Daily Logs"
                        disableReview
                        value={contractorDailyLogs}
                        setValue={setContractorDailyLogs}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Field Reports"
                        disableReview
                        value={fieldReports}
                        setValue={setFieldReports}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="CA Meeting Minutes"
                        disableReview
                        value={meetingMinutes}
                        setValue={setMeetingMinutes}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Miscellaneous Documents"
                        disableReview
                        value={miscellaneousDocuments}
                        setValue={setMiscellaneousDocuments}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Schedules"
                        disableReview
                        value={schedules}
                        setValue={setSchedules}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Testing"
                        disableReview
                        value={testing}
                        setValue={setTesting}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Consultant Review"
                        value={additionalReview}
                        setValue={setAdditionalReview}
                      />

                      <Divider
                        style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                      />
                      <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                        Close Out
                      </Typography>

                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Punch List"
                        value={punchList}
                        setValue={setPunchList}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="As-Built"
                        value={asBuilt}
                        setValue={setAsBuilt}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Closeout Submittals"
                        value={closeoutSubmittals}
                        setValue={setCloseoutSubmittals}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="O&M Data"
                        disableReview
                        value={OMData}
                        setValue={setOMData}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Warranty Items"
                        value={warrantyItems}
                        setValue={setWarrantyItems}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Substantial Completion Cert."
                        disableReview
                        value={substantialCompletionCert}
                        setValue={setSubstantialCompletionCert}
                      />
                      <PermissionControl
                        disabled={mode === FormMode.NONE}
                        label="Clear L&P"
                        disableReview
                        value={clearLP}
                        setValue={setClearLP}
                      />

                      {subscriber?.canViewProjectLifecycle ? (
                        <>
                          <Divider
                            style={{ marginTop: 16, marginBottom: 16, opacity: isMobile ? 0 : 100 }}
                          />
                          <Typography variant="subtitle2" style={{ paddingBottom: 16 }}>
                            Storytelling
                          </Typography>

                          <PermissionControl
                            label="Storytelling Tab"
                            disabled={mode === FormMode.NONE}
                            value={projectLifecycle}
                            disableComment
                            disableCreate
                            customLabels={{ reviewLabel: 'Create/Edit' }}
                            setValue={
                              setProjectLifecycle as React.Dispatch<
                                React.SetStateAction<SecurityPermissionLevel | null>
                              >
                            }
                          />
                        </>
                      ) : null}
                    </div>
                  </Grid>
                </Grid>
              </div>
            </form>
          </SecurityGroupsCard>
        </Grid>
      </Grid>
      <DeleteSecurityGroupDialog
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        transferAndDelete={transferUsersAndDelete}
        securityGroups={securityGroups.filter(
          (sg) => sg.id !== selectedGroupId && sg.id !== subscriber?.companyViewSecurityGroupId,
        )}
        summary={securityGroupSummary}
      />
    </main>
  );
}
